import { datadogRum } from "@datadog/browser-rum";
import { getEnvironment } from "../../Config/utils/enviroment";
import { telemetryEnv } from "../utils/telemetryEnv";
import { DappleVersionNumber } from "../../../VersionNumber";
import { TelemetryLevel, getBootTelemetryLevel } from "../utils/telemetryLevel";
import { applyTelemetryLevel } from "./applyTelemetryLevel";
import { scrubRumEventUrls } from "../utils/rumUrlScrub";
import { scrubUrl } from "../../Consent/utils/urlScrub";
import { hasConsent, subscribeConsent } from "../../Consent/state/consentStore";
import { getEffectiveTelemetryLevel } from "../state/telemetryLevelStore";
import { isThirdPartyError } from "../utils/thirdPartyErrors";
import { isApolloDevError } from "../utils/apolloDevErrors";
import {
  TransportFailure,
  transportFailureOf,
} from "../utils/transportFailureErrors";
import { addClientFaultAction } from "../index";

const BOT_PATTERN =
  /bot|crawl|spider|HeadlessChrome|Googlebot|bingbot|Baiduspider|yandex|slurp|facebookexternalhit|Twitterbot|LinkedInBot|Semrush|DotBot|AhrefsBot|MJ12bot|PetalBot/i;

export const init = () => {
  // This device's setting combined with the kill switch LaunchDarkly gave us
  // last session (DHT-5279). Read synchronously, because this runs at module
  // scope from the app entries before React exists.
  //
  // The cached kill switch is what makes the remote switch effective from the
  // first millisecond. LD itself cannot answer this early, and on web it is
  // deferred until the tenant context resolves after login, so without the
  // cache a killed tenant would have its whole login recorded on every load.
  // `useTelemetryGate` refreshes the cache and applies any change once
  // LD does answer.
  const telemetryLevel = getBootTelemetryLevel();

  // Tracking consent (GDPR opt-in brands, CONSENT.md). The consent store reads
  // the brand config the backend injected into the HTML, and the visitor's
  // `dapple_consent` cookie, synchronously at import, so this is the answer
  // before the SDK sends anything. NotRequired brands, the clinic app and every
  // page without the injection read as consented, which is today's behaviour.
  //
  // Without analytics consent the SDK starts `not-granted` (no session, no
  // events) and Session Replay waits to be started by hand, so a later grant
  // can start the recorder at the configured sample rate. See
  // `installConsentSync`.
  const analyticsConsented = hasConsent("analytics");

  datadogRum.init({
    applicationId: "5676abc7-dbf7-4ace-9f03-a5040500d3ed",
    clientToken: "pub7301ffe077c7c4de6bbfe7817d8283a1",
    site: "us5.datadoghq.com",
    service: "web",
    env: telemetryEnv(getEnvironment(), __DEV__),
    trackSessionAcrossSubdomains: true,
    sessionSampleRate: 100,
    // Drawn after sessionSampleRate, so with that at 100 this is the absolute
    // share of sessions that record. 0 means the recorder never starts, which
    // is the point of the `noReplay` rung: no DOM mirror, no mutation
    // observers, no deflate worker.
    sessionReplaySampleRate: telemetryLevel === "full" ? 100 : 0,
    trackingConsent:
      telemetryLevel !== "off" && analyticsConsented
        ? "granted"
        : "not-granted",
    startSessionReplayRecordingManually: !analyticsConsented,
    traceSampleRate: 20,
    // "mask" replaces every rendered text node with "***" and every image with
    // a 1x1 transparent gif before the replay leaves the browser. Both web
    // entry points record 100% of sessions to Datadog's US site, and one of
    // them is the patient portal, so "mask-user-input" was shipping rendered
    // clinical records offshore. Layout, click targets, navigation, timings and
    // errors are unaffected, so a replay still shows what someone did.
    defaultPrivacyLevel: "mask",
    version: DappleVersionNumber,
    allowedTracingUrls: [
      "https://api.dapple.dev",
      "https://api-au.dapplecrm.com",
      "https://app-au.dapplecrm.com",
      "https://app-au-next.dapplecrm.com",
      "https://360.juvae.com.au",
      "https://glow.dapple.dev",
      "https://localhost:3000",
    ],
    beforeSend: event => {
      if (event.type === "error" && BOT_PATTERN.test(navigator.userAgent)) {
        return false;
      }
      // Every mode: no query parameter except the tracking allowlist, and no
      // fragment, reaches Datadog. Portal URLs carry `?token=` credentials.
      scrubRumEventUrls(event);
      if (event.type !== "error") return true;
      // Extensions, cross-origin "Script error." and tenant GTM tags (DHT-5531).
      if (isThirdPartyError(event)) return false;
      // Apollo dev invariants from bundles built before DHT-5552.
      if (isApolloDevError(event)) return false;
      // A browser's dropped-connection wording with nothing else to it is the
      // device's network, not a fault of ours (DHT-5802): count it, don't
      // raise it.
      const transportFailure = transportFailureOf(event);
      if (transportFailure) {
        reportTransportFailure(transportFailure);
        return false;
      }
      return true;
    },
  });

  installConsentSync(telemetryLevel);

  // Framed-detection probe (audit G2, 2026-08-05): customers embed their
  // portals in iframes on their own websites, which is why no frame-ancestors
  // CSP is enforced. This records WHO actually frames us (document.referrer =
  // the embedding page on initial load) so any future clickjacking policy can
  // be allowlisted from data instead of folklore.
  // Datadog: @context.framed:true / action "portal_framed".
  try {
    if (window.self !== window.top) {
      datadogRum.setGlobalContextProperty("framed", true);
      datadogRum.addAction("portal_framed", {
        // Scrubbed like every other URL: the embedding page's own query
        // string is not ours to send offshore.
        embeddingReferrer: scrubUrl(document.referrer) || "unknown",
      });
    }
  } catch {
    // The identity comparison is cross-origin-safe, but a framing probe must
    // never be able to break app boot.
  }
};

/**
 * Records a transport failure `beforeSend` dropped, as the same
 * `network_request_failed` client-fault action the GraphQL links (DHT-5616)
 * and image uploads (DHT-5748) send, told apart by `client: "rum-error"`.
 *
 * Deferred because `beforeSend` runs while the SDK is assembling an event, and
 * adding an action from inside it would re-enter the SDK mid-event. Caught
 * because nothing scheduled from the error path may throw.
 */
function reportTransportFailure(failure: TransportFailure) {
  setTimeout(() => {
    try {
      addClientFaultAction("network_request_failed", {
        client: "rum-error",
        ...failure,
      });
    } catch {
      // Losing one count is better than an error thrown from error handling.
    }
  }, 0);
}

/**
 * Follows the visitor's analytics consent for the rest of the page's life:
 * granting it turns the SDK on (and starts Session Replay where the telemetry
 * level allows it), withdrawing it sets `not-granted` and stops the recorder.
 *
 * Module level rather than in a React hook because consent can change on pages
 * that never mount the telemetry gate (the standalone token pages), and because
 * the SDK it drives was started here. The telemetry level is combined in
 * `applyTelemetryLevel`, which reads consent as well, so the gate and this
 * listener can never disagree.
 */
function installConsentSync(bootLevel: TelemetryLevel) {
  let lastAnalytics = hasConsent("analytics");

  subscribeConsent(() => {
    const analytics = hasConsent("analytics");
    if (analytics === lastAnalytics) return;
    lastAnalytics = analytics;

    applyTelemetryLevel(getEffectiveTelemetryLevel(), bootLevel);
  });
}
